import React, { ReactNode, useEffect, useMemo, useState } from "react";
import Select, { Props as SelectProps } from "react-select";
import { Form } from "react-bootstrap";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
const errorMessage: string = require("../../../images/error-message-white.png");

// Define the shape of the options
interface OptionType {
  value: string | number;
  label: string;
}

// Define the props based on the SelectProps generic type
interface DropdownApiProps extends SelectProps<OptionType> {
  label: ReactNode;
  className?: string;
  width?: string;
  updateRef?: boolean;
  isMulti?: boolean;
  placeholder?: string;
  required?: boolean;
  value?: OptionType;
  defaultValue?: OptionType;
  isInvalid?: boolean;
  isDisabled?: boolean;
  getDataHook?: any;
  labelAttribute?: string;
}

const useFetchMyApi = (getDataHook, searchText, labelAttribute: string) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [items, setItems] = useState<any[] | []>([]);
  // Only send filter[name] when the user is actively searching. Many v2
  // endpoints (e.g. /v2/phases) don't declare filter[name] and 400 on it,
  // which would leave the dropdown with no options on initial load.
  let { data, error, isLoading, refetch } = getDataHook(
    searchText
      ? { slug: accountSlug, filter: { name: searchText } }
      : { slug: accountSlug }
  );
  useEffect(() => {
    setTimeout(() =>
      setItems(
        (data?.data || []).map((item) => {
          return {
            label: item?.attributes?.[labelAttribute] || "",
            value: item?.id?.toString() || "",
          };
        })
      )
    );
  }, [searchText]);
  if (data) {
    return (data?.data || []).map((item) => {
      return {
        label: item?.attributes?.[labelAttribute] || "",
        value: item?.id?.toString() || "",
      };
    });
  } else {
    return [items];
  }
};

const DropdownApi: React.FC<DropdownApiProps> = ({
  value,
  onChange,
  onInputChange,
  label,
  className,
  width = "100%",
  placeholder,
  required,
  defaultValue,
  isInvalid,
  onFocus,
  isDisabled = false,
  getDataHook,
  labelAttribute = "name",
}) => {
  const [searchText, setSearchText] = useState("");
  const options = useFetchMyApi(getDataHook, searchText, labelAttribute);

  // Resolve the displayed option from `options` by id, so callers only need
  // to track the id. When the option list arrives, the label snaps to its
  // canonical value; before then, fall back to whatever the caller passed.
  const resolvedValue = useMemo<OptionType | undefined>(() => {
    if (!value || value.value === 0 || value.value == null) return undefined;
    const match = (options as OptionType[]).find(
      (o) => String(o.value) === String(value.value)
    );
    return match ?? value;
  }, [value, options]);

  const onInputChangeFunction = (e) => {
    if (e?.length > 2) {
      setSearchText(e);
    }
    if (onInputChange) {
      onInputChange(e.target.value, {
        action: "set-value",
        prevInputValue: "",
      });
    }
  };

  return (
    <Form.Group
      className="formGroup"
      style={isInvalid ? { marginBottom: "0px" } : {}}
    >
      <Form.Label>{label}</Form.Label>
      <Select<OptionType>
        className={className}
        placeholder={placeholder}
        defaultValue={defaultValue}
        value={resolvedValue}
        onChange={onChange}
        onInputChange={(e) => onInputChangeFunction(e)}
        options={options as any[]}
        isMulti={false as any}
        required={required}
        onFocus={onFocus}
        isDisabled={isDisabled}
      />
      {required && isInvalid ? (
        <div>
          <img
            src={errorMessage}
            alt="Please fill out this field error message"
          />
        </div>
      ) : null}
    </Form.Group>
  );
};

export default DropdownApi;
